/**
 * driving_log_options — "주행 데이터 로깅" 으로 무엇을 남길지 고르는 선택 상태.
 *
 * 예전 로거는 무엇을 쓸지 코드에 박아두어, 필요한 항목을 추가하거나 잡음을 빼려면
 * 매번 코드를 고쳐야 했다. 여기서는 카탈로그(카테고리 → 항목)와 선택 집합을 분리해,
 * 운영자가 모달에서 체크한 항목만 기록하도록 한다.
 *
 * camera_fps_cap_store 와 같은 패턴(useSyncExternalStore 싱글턴)이다. 무거운 의존성
 * (brain-proto/mqtt)을 끌어오지 않아야 로거·탭(tap)·테스트 어디서나 싸게 참조된다.
 *
 * 항목 id 는 localStorage 에 그대로 저장되므로 **바뀌면 안 된다**. 카테고리 접두어를
 * 붙여(`rx.` / `ui.` / `tx.` / `debug.`) 카테고리가 달라도 충돌하지 않게 했다.
 */
import { useSyncExternalStore } from "react";
import {
  BRAIN_TOPICS_CAMERA_SETTINGS,
  BRAIN_TOPICS_COMMAND,
  BRAIN_TOPICS_CONTROL,
  BRAIN_TOPICS_HEARTBEAT,
  BRAIN_TOPICS_REMOTE_STATUS,
  BRAIN_TOPICS_STATUS,
  BRAIN_TOPICS_SYSTEM,
} from "../config/mqtt_config";

export type DrivingLogCategoryId = "rx" | "ui" | "tx" | "debug";

export interface DrivingLogItem {
  /** 저장 키 — 절대 바꾸지 않는다. */
  id: string;
  label: string;
  /** rx/tx 항목만: 대응하는 Brain 토픽. */
  topic?: string;
  /** 기본 체크 여부("core only"). */
  byDefault?: boolean;
}

export interface DrivingLogCategory {
  id: DrivingLogCategoryId;
  label: string;
  hint: string;
  items: readonly DrivingLogItem[];
}

export const DRIVING_LOG_STORAGE_KEY = "aw.drivingLogOptions.v1";

export const DRIVING_LOG_CATALOG: readonly DrivingLogCategory[] = Object.freeze([
  {
    id: "rx",
    label: "수신 토픽",
    hint: "차량 → 콕핏 · 도착할 때마다 한 줄",
    items: [
      { id: "rx.control", label: "제어 상태 (control)", topic: BRAIN_TOPICS_STATUS.CONTROL, byDefault: true },
      { id: "rx.state", label: "차량 상태 (state)", topic: BRAIN_TOPICS_STATUS.STATE, byDefault: true },
      { id: "rx.automode", label: "자율주행 모드 (automode)", topic: BRAIN_TOPICS_STATUS.AUTOMODE, byDefault: true },
      { id: "rx.remote_control", label: "원격제어 상태 (remote_control)", topic: BRAIN_TOPICS_STATUS.REMOTE_CONTROL, byDefault: true },
      { id: "rx.control_mode", label: "제어 모드 (control_mode)", topic: BRAIN_TOPICS_STATUS.CONTROL_MODE, byDefault: true },
      { id: "rx.vehicle_info", label: "차량 정보 (vehicle_info)", topic: BRAIN_TOPICS_STATUS.VEHICLE_INFO, byDefault: true },
      { id: "rx.vehicle_request", label: "차량 제어권 요청 (vehicle_request)", topic: BRAIN_TOPICS_STATUS.VEHICLE_REQUEST },
      { id: "rx.camera_params", label: "카메라 파라미터 (camera_params)", topic: BRAIN_TOPICS_STATUS.CAMERA_PARAMS },
      { id: "rx.kv", label: "키-값 (kv)", topic: BRAIN_TOPICS_STATUS.KV },
      { id: "rx.kv_array", label: "키-값 배열 (kv_array)", topic: BRAIN_TOPICS_STATUS.KV_ARRAY },
      { id: "rx.waypoints", label: "경로점 (waypoints)", topic: BRAIN_TOPICS_STATUS.WAYPOINTS },
      { id: "rx.routing_req", label: "경로 요청 (routing_req)", topic: BRAIN_TOPICS_STATUS.ROUTING_REQ },
      { id: "rx.mission", label: "미션 (mission)", topic: BRAIN_TOPICS_STATUS.MISSION },
      { id: "rx.mrm_status", label: "MRM 상태 (mrm_status)", topic: BRAIN_TOPICS_STATUS.MRM_STATUS },
      { id: "rx.handback", label: "제어 반납 (handback)", topic: BRAIN_TOPICS_STATUS.HANDBACK },
      { id: "rx.config", label: "차량 설정 (config)", topic: BRAIN_TOPICS_STATUS.CONFIG },
      { id: "rx.vehicle_ack_info", label: "차량 ACK (vehicle_ack_info)", topic: BRAIN_TOPICS_STATUS.VEHICLE_ACK_INFO },
      { id: "rx.heartbeat", label: "하트비트 핑 (heartbeat)", topic: BRAIN_TOPICS_HEARTBEAT.PING },
      { id: "rx.cam_front", label: "카메라 설정 · 전방", topic: BRAIN_TOPICS_CAMERA_SETTINGS.FRONT },
      { id: "rx.cam_left", label: "카메라 설정 · 좌측", topic: BRAIN_TOPICS_CAMERA_SETTINGS.LEFT },
      { id: "rx.cam_right", label: "카메라 설정 · 우측", topic: BRAIN_TOPICS_CAMERA_SETTINGS.RIGHT },
      { id: "rx.cam_left_rear", label: "카메라 설정 · 좌후방", topic: BRAIN_TOPICS_CAMERA_SETTINGS.LEFT_REAR },
      { id: "rx.cam_right_rear", label: "카메라 설정 · 우후방", topic: BRAIN_TOPICS_CAMERA_SETTINGS.RIGHT_REAR },
      { id: "rx.cam_rear", label: "카메라 설정 · 후방", topic: BRAIN_TOPICS_CAMERA_SETTINGS.REAR },
    ],
  },
  {
    id: "ui",
    label: "UI 측정값",
    hint: "콕핏에서 재는 값 · 1초마다 한 줄",
    items: [
      { id: "ui.rtt", label: "왕복 지연 (RTT)", byDefault: true },
      { id: "ui.camera_fps", label: "카메라 수신 FPS", byDefault: true },
      { id: "ui.link_stats", label: "WebRTC 링크 통계 (getStats)", byDefault: true },
      { id: "ui.position", label: "차량 위치", byDefault: true },
      { id: "ui.vehicle", label: "차량 상태 요약" },
      { id: "ui.jerk", label: "저크 (가속도 미분)" },
      { id: "ui.wheel_sample", label: "휠 조향 샘플" },
      { id: "ui.screen_mode", label: "화면 모드 (모니터링/몰입/전체화면)" },
      { id: "ui.external_display", label: "보조 디스플레이 상태" },
    ],
  },
  {
    id: "tx",
    label: "송신 토픽",
    hint: "콕핏 → 차량 · 보낼 때마다 한 줄",
    items: [
      { id: "tx.lateral", label: "조향 명령 (lateral)", topic: BRAIN_TOPICS_COMMAND.LATERAL, byDefault: true },
      { id: "tx.longitudinal", label: "가감속 명령 (longitudinal)", topic: BRAIN_TOPICS_COMMAND.LONGITUDINAL, byDefault: true },
      { id: "tx.system", label: "시스템 명령 (system)", topic: BRAIN_TOPICS_COMMAND.SYSTEM, byDefault: true },
      { id: "tx.control_request", label: "제어권 요청 (control/request)", topic: BRAIN_TOPICS_CONTROL.REQUEST, byDefault: true },
      { id: "tx.accessory", label: "부가장치 명령 (accessory)", topic: BRAIN_TOPICS_COMMAND.ACCESSORY },
      { id: "tx.path", label: "경로 명령 (path)", topic: BRAIN_TOPICS_COMMAND.PATH },
      { id: "tx.mission", label: "미션 명령 (mission)", topic: BRAIN_TOPICS_COMMAND.MISSION },
      { id: "tx.automode", label: "자율주행 전환 (automode)", topic: BRAIN_TOPICS_COMMAND.AUTOMODE },
      { id: "tx.kv", label: "키-값 (kv)", topic: BRAIN_TOPICS_COMMAND.KV },
      { id: "tx.kv_array", label: "키-값 배열 (kv_array)", topic: BRAIN_TOPICS_COMMAND.KV_ARRAY },
      { id: "tx.user_configuration", label: "사용자 설정 (user_configuration)", topic: BRAIN_TOPICS_COMMAND.USER_CONFIGURATION },
      { id: "tx.media", label: "미디어 명령 (media)", topic: BRAIN_TOPICS_COMMAND.MEDIA },
      { id: "tx.heartbeat_ack", label: "하트비트 응답 (heartbeat_ack)", topic: BRAIN_TOPICS_CONTROL.HEARTBEAT_ACK },
      { id: "tx.cock_hw", label: "콕핏 하드웨어 상태 (cock_hw)", topic: BRAIN_TOPICS_REMOTE_STATUS.COCKPIT_HARDWARE },
      { id: "tx.remote_system", label: "콕핏 시스템 상태 (remote/status/system)", topic: BRAIN_TOPICS_REMOTE_STATUS.SYSTEM },
      { id: "tx.ack_info", label: "콕핏 ACK (ack_info)", topic: BRAIN_TOPICS_SYSTEM.REMOTE_ACK_INFO },
    ],
  },
  {
    id: "debug",
    label: "디버깅 정보",
    hint: "조작·창 상태 · 바뀔 때마다 한 줄",
    items: [
      { id: "debug.controller_input", label: "휠/게임패드 입력 (조향·가속·제동)", byDefault: true },
      { id: "debug.keyboard", label: "키보드 입력 (모든 키)" },
      { id: "debug.controller_state", label: "컨트롤러 선택 · 연결 상태" },
      { id: "debug.panel_state", label: "디버그 창 열림 상태" },
      { id: "debug.camera_fault", label: "영상 장애 모사 설정" },
      { id: "debug.fps_cap", label: "카메라 FPS 상한 설정" },
      { id: "debug.wheel_usb", label: "휠 USB 연결/해제" },
    ],
  },
]);

/** 카탈로그에 실제로 존재하는 id 만 받아들이기 위한 색인. */
const KNOWN_ITEM_IDS: ReadonlySet<string> = new Set(
  DRIVING_LOG_CATALOG.flatMap((category) => category.items.map((item) => item.id)),
);

export const DRIVING_LOG_DEFAULT_ITEM_IDS: readonly string[] = Object.freeze(
  DRIVING_LOG_CATALOG.flatMap((category) =>
    category.items.filter((item) => item.byDefault).map((item) => item.id),
  ),
);

function buildTopicIndex(category: DrivingLogCategoryId): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  DRIVING_LOG_CATALOG.find((c) => c.id === category)?.items.forEach((item) => {
    if (item.topic) {
      index.set(item.topic, item.id);
    }
  });
  return index;
}

/** Brain 토픽 → 항목 id. 탭(tap)이 매 메시지마다 쓰므로 미리 만들어 둔다. */
export const DRIVING_LOG_RX_ITEM_BY_TOPIC = buildTopicIndex("rx");
export const DRIVING_LOG_TX_ITEM_BY_TOPIC = buildTopicIndex("tx");

function readStoredSelection(): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(DRIVING_LOG_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { items?: unknown };
    if (!Array.isArray(parsed?.items)) {
      return null;
    }
    // 카탈로그에서 사라진 id(옛 버전에서 저장된 것)는 조용히 버린다.
    return new Set(
      parsed.items.filter(
        (id): id is string => typeof id === "string" && KNOWN_ITEM_IDS.has(id),
      ),
    );
  } catch {
    return null;
  }
}

let selection: ReadonlySet<string> =
  readStoredSelection() ?? new Set(DRIVING_LOG_DEFAULT_ITEM_IDS);
const listeners = new Set<() => void>();

function persist(): void {
  try {
    window.localStorage.setItem(
      DRIVING_LOG_STORAGE_KEY,
      JSON.stringify({ v: 1, items: Array.from(selection).sort() }),
    );
  } catch {
    // 시크릿 모드/저장 용량 초과 — 이번 세션 선택은 메모리로만 유지한다.
  }
}

function commit(next: Set<string>): void {
  selection = next;
  persist();
  listeners.forEach((listener) => listener());
}

export function getDrivingLogSelection(): ReadonlySet<string> {
  return selection;
}

export function isDrivingLogItemEnabled(itemId: string): boolean {
  return selection.has(itemId);
}

export function setDrivingLogItemEnabled(itemId: string, enabled: boolean): void {
  if (!KNOWN_ITEM_IDS.has(itemId) || selection.has(itemId) === enabled) {
    return;
  }
  const next = new Set(selection);
  if (enabled) {
    next.add(itemId);
  } else {
    next.delete(itemId);
  }
  commit(next);
}

/** 카테고리 전체 선택/해제 — 모달의 "전체 선택 / 해제" 버튼. */
export function setDrivingLogCategoryEnabled(
  categoryId: DrivingLogCategoryId,
  enabled: boolean,
): void {
  const category = DRIVING_LOG_CATALOG.find((c) => c.id === categoryId);
  if (!category) {
    return;
  }
  const next = new Set(selection);
  category.items.forEach((item) => {
    if (enabled) {
      next.add(item.id);
    } else {
      next.delete(item.id);
    }
  });
  if (next.size === selection.size) {
    return;
  }
  commit(next);
}

/** 기본값("core only")으로 되돌린다. */
export function resetDrivingLogSelection(): void {
  commit(new Set(DRIVING_LOG_DEFAULT_ITEM_IDS));
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/** 선택 집합을 구독한다(체크 변경 시 리렌더). */
export function useDrivingLogSelection(): ReadonlySet<string> {
  return useSyncExternalStore(
    subscribe,
    getDrivingLogSelection,
    getDrivingLogSelection,
  );
}

/** 테스트 전용 — 모듈 싱글턴을 저장소에서 다시 읽어 초기화한다. */
export function reloadDrivingLogSelectionForTests(): void {
  selection = readStoredSelection() ?? new Set(DRIVING_LOG_DEFAULT_ITEM_IDS);
  listeners.forEach((listener) => listener());
}
